import { createSlice } from "@reduxjs/toolkit";
import {
  fetchTemplatesThunk,
  createTemplateThunk,
  updateTemplateThunk,
  cloneTemplateThunk,
  archiveTemplateThunk,
  unarchiveTemplateThunk,
  validateTemplateThunk,
  deleteTemplateThunk,
} from "../thunks/templateThunks";

const initialState = {
  list: [],
  selected: null,
  loading: false,
  error: null,
  validationResult: null,
  filters: {
    search: "",
    archived: false,
    locale: "en-IN",
    isPromotional: undefined,
  },
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false,
  },
};

// Helper function to transform backend template data to frontend format
const transformTemplateData = (backendTemplate) => {
  if (!backendTemplate) return null;

  return {
    // Core fields from backend
    _id: backendTemplate._id,
    key: backendTemplate.key,
    version: backendTemplate.version || 1,
    locale: backendTemplate.locale || "en-IN",

    // Map backend fields
    name: backendTemplate.name || backendTemplate.key,
    description:
      backendTemplate.description || `Template ${backendTemplate.key}`,

    // Content fields
    titleTpl: backendTemplate.titleTpl,
    bodyTpl: backendTemplate.bodyTpl,
    imageUrlTpl: backendTemplate.imageUrlTpl || "",
    iconUrlTpl: backendTemplate.iconUrlTpl || "",
    deeplinkTpl: backendTemplate.deeplinkTpl || "",

    // Actions
    actions: backendTemplate.actions || [],

    // Metadata
    isPromotional: backendTemplate.isPromotional || false,
    channels: backendTemplate.channels || ["FCM", "APNS", "WEB"],
    category: backendTemplate.category || "TRANSACTIONAL",
    priority: backendTemplate.priority || "NORMAL",
    tags: backendTemplate.tags || [],

    // Variables metadata
    variables: backendTemplate.variables || [],

    // Status
    archived: backendTemplate.archived || false,

    // Usage tracking
    usageCount: backendTemplate.usageCount || 0,
    lastUsedAt: backendTemplate.lastUsedAt,

    // Timestamps
    createdAt: backendTemplate.createdAt,
    updatedAt: backendTemplate.updatedAt,

    // User references
    createdBy: backendTemplate.createdBy,
    updatedBy: backendTemplate.updatedBy,

    // Computed display field for UI
    platforms:
      backendTemplate.channels?.map((ch) => {
        switch (ch) {
          case "FCM":
            return "Android";
          case "APNS":
            return "iOS";
          case "WEB":
            return "Web";
          case "WHATSAPP":
            return "WhatsApp";
          default:
            return ch;
        }
      }) || [],
  };
};

const templateSlice = createSlice({
  name: "notificationTemplates",
  initialState,
  reducers: {
    setSelectedTemplate: (state, action) => {
      state.selected = action.payload;
    },
    clearSelectedTemplate: (state) => {
      state.selected = null;
    },
    setTemplateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearTemplateError: (state) => {
      state.error = null;
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
    },
    resetTemplates: (state) => {
      state.list = [];
      state.selected = null;
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    // Fetch templates
    builder
      .addCase(fetchTemplatesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplatesThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        let templates = [];
        let pagination = state.pagination;

        // Handle different response structures from backend
        if (response.ok && response.templates) {
          // New backend format: { ok: true, templates: [...], pagination: {...} }
          templates = response.templates.map(transformTemplateData);
          if (response.pagination) {
            pagination = {
              ...pagination,
              total: response.pagination.total || templates.length,
              hasMore: response.pagination.hasMore || false,
            };
          }
        } else if (response.ok && response.data) {
          // Alternative format: { ok: true, data: [...] }
          templates = response.data.map(transformTemplateData);
        } else if (Array.isArray(response)) {
          // Direct array response
          templates = response.map(transformTemplateData);
        }

        state.list = templates;
        state.pagination = pagination;
      })
      .addCase(fetchTemplatesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch templates";
        state.list = [];
      });

    // Create template
    builder
      .addCase(createTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        let savedTemplate = null;

        if (response.ok && response.template) {
          savedTemplate = transformTemplateData(response.template);
        } else if (response.data) {
          savedTemplate = transformTemplateData(response.data);
        } else {
          savedTemplate = transformTemplateData(response);
        }

        if (savedTemplate) {
          // Add new template to the beginning of the list
          state.list.unshift(savedTemplate);
          state.selected = savedTemplate;
        }
      })
      .addCase(createTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create template";
      });

    // Update template
    builder
      .addCase(updateTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        let updatedTemplate = null;

        if (response.ok && response.template) {
          updatedTemplate = transformTemplateData(response.template);
        } else if (response.data) {
          updatedTemplate = transformTemplateData(response.data);
        } else {
          updatedTemplate = transformTemplateData(response);
        }

        if (updatedTemplate) {
          const index = state.list.findIndex(
            (t) =>
              t.key === updatedTemplate.key &&
              t.locale === updatedTemplate.locale
          );

          if (index >= 0) {
            state.list[index] = updatedTemplate;
          }

          state.selected = updatedTemplate;
        }
      })
      .addCase(updateTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update template";
      });

    // Clone template
    builder
      .addCase(cloneTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cloneTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        let clonedTemplate = null;

        if (response.ok && response.data) {
          clonedTemplate = transformTemplateData(response.data);
        } else {
          clonedTemplate = transformTemplateData(response);
        }

        if (clonedTemplate) {
          state.list.unshift(clonedTemplate);
        }
      })
      .addCase(cloneTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to clone template";
      });

    // Archive template
    builder
      .addCase(archiveTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        if (response) {
          // Update the template in the list
          const { key, version } = action.payload;
          const index = state.list.findIndex(
            (t) => t.key === key && (!version || t.version === version)
          );

          if (index >= 0) {
            if (state.filters.archived) {
              // Keep in list if showing archived templates
              state.list[index] = { ...state.list[index], archived: true };
            } else {
              // Remove from list if not showing archived templates
              state.list.splice(index, 1);
            }
          }
        }
      })
      .addCase(archiveTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to archive template";
      });

    // Unarchive template
    builder
      .addCase(unarchiveTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unarchiveTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;
        if (response) {
          // Update the template in the list
          const { key, version } = action.payload;
          const index = state.list.findIndex(
            (t) => t.key === key && (!version || t.version === version)
          );

          if (index >= 0) {
            if (!state.filters.archived) {
              // Keep in list if showing active templates
              state.list[index] = { ...state.list[index], archived: false };
            } else {
              // Remove from list if only showing archived templates
              state.list.splice(index, 1);
            }
          }
        }
      })
      .addCase(unarchiveTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to unarchive template";
      });

    // Delete template
    builder
      .addCase(deleteTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { key, version } = action.meta.arg;

        // Remove the template from the list
        if (version) {
          // Remove specific version
          state.list = state.list.filter(
            (t) => !(t.key === key && t.version === version)
          );
        } else {
          // Remove all versions of the template
          state.list = state.list.filter((t) => t.key !== key);
        }

        // Clear selected template if it was deleted
        if (state.selected && state.selected.key === key) {
          if (!version || state.selected.version === version) {
            state.selected = null;
          }
        }
      })
      .addCase(deleteTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete template";
      });

    // Validate template
    builder
      .addCase(validateTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.validationResult = action.payload;
      })
      .addCase(validateTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to validate template";
      });
  },
});

export const {
  setSelectedTemplate,
  clearSelectedTemplate,
  setTemplateFilters,
  clearTemplateError,
  clearValidationResult,
  resetTemplates,
} = templateSlice.actions;

export default templateSlice.reducer;
